import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useSessionContext } from '@/auth/useSessionContext';
import { BLACK_NOVEMBER_SLUG, fetchCampaignProgressService } from '@/services/campaign';
import { BACKGROUND_QUERY_META } from '@/utils/backgroundQuery';
import { isResultsPhase, parseCampaignProgress } from '@/utils/blackNovemberCampaign';
import { usePendingRegistration } from './usePendingRegistration';

const DISMISS_KEY = 'blackNovember2026ResultDismissed';

/** O backend cacheia a soma por 5 minutos; o painel não precisa perguntar antes. */
const STALE_TIME = 5 * 60 * 1000;

const readDismissed = () => {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === 'true';
  } catch (error) {
    // Navegador com armazenamento bloqueado: a faixa aparece, e só.
    return false;
  }
};

const persistDismissed = () => {
  try {
    window.localStorage.setItem(DISMISS_KEY, 'true');
  } catch (error) {
    // Sem persistir, o × vale para a sessão atual — melhor que quebrar o header.
  }
};

export const useBlackNovemberCampaign = () => {
  const { user } = useSessionContext();
  const { shouldShowPendingRegistrationAlert } = usePendingRegistration();
  const [dismissed, setDismissed] = useState(readDismissed);

  // Quem vê a faixa é o backend que decide: a função `black_november` (piloto) ou o
  // switch `black_november_everyone` (base inteira). Lê o campo recalculado a cada
  // `/user/`, e não `experimental_features`, que fica uma hora em cache: abrir para a
  // base vale no próximo load.
  const isEnabled = user?.blackNovemberEnabled === true;

  const { data } = useQuery({
    queryKey: ['campaign', BLACK_NOVEMBER_SLUG],
    queryFn: ({ signal }) =>
      fetchCampaignProgressService({ slug: BLACK_NOVEMBER_SLUG, signal }),
    // Sem a liberação não há request: quem não vê a faixa não paga a chamada,
    // e o header não depende de uma resposta que não vai usar.
    enabled: isEnabled,
    staleTime: STALE_TIME,
    // O header não é lugar de erro: se a campanha não responde, ela não aparece.
    retry: false,
    meta: BACKGROUND_QUERY_META,
  });

  const progress = useMemo(() => parseCampaignProgress(data), [data]);

  const dismiss = useCallback(() => {
    persistDismissed();
    setDismissed(true);
  }, []);

  const isResults = isResultsPhase(progress?.phase);

  return {
    progress,
    dismiss,
    /**
     * Só o resultado é dispensável. Durante a campanha, fechar a faixa é abrir
     * mão de acompanhar a meta, então o × nem aparece.
     */
    isDismissible: isResults,
    /**
     * O alerta de cadastro pendente mora no mesmo AppBar e tem prioridade — é
     * dinheiro parado. Com ele na tela a faixa do header cai para a versão
     * curta, sem a barra de progresso, em vez de disputar a largura.
     */
    isCompact: shouldShowPendingRegistrationAlert === true,
    isVisible: Boolean(progress) && !(isResults && dismissed),
  };
};
